import React, {useEffect, useRef, useState} from 'react';
import {Link, useNavigate, useParams, useSearchParams} from 'react-router-dom';
import Layout from '../components/Layout';
import ConfirmModal from '../../components/ConfirmModal';

import {STATE_LABELS} from '../constants/address';
import {api, API_BASE, getCookie} from '../../api';

const FILE_TYPES = {
  'application/pdf': {color: 'text-red-600', bg: 'bg-red-50', label: 'PDF'},
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {color: 'text-blue-600', bg: 'bg-blue-50', label: 'DOCX'},
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {color: 'text-green-600', bg: 'bg-green-50', label: 'XLSX'},
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': {color: 'text-orange-600', bg: 'bg-orange-50', label: 'PPTX'},
};

function FileTypeBadge({contentType, fileName}) {
  const match = FILE_TYPES[contentType];
  const ext = fileName?.split('.').pop()?.toUpperCase() || '?';
  const color = match?.color || 'text-gray-600';
  const bg = match?.bg || 'bg-gray-50';
  const label = match?.label || ext;
  return (
    <span className={`inline-flex items-center px-2 py-1 rounded ${bg} ${color} text-xs font-medium`}>
      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
      {label}
    </span>
  );
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}


export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [credForm, setCredForm] = useState(null);
  const [credSaving, setCredSaving] = useState(false);
  const [deletingCred, setDeletingCred] = useState(null);
  const credFileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [createName, setCreateName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolder = searchParams.get('folder') || null;
  const setCurrentFolder = (folderId) => {
    const params = new URLSearchParams(searchParams);
    if (folderId) params.set('folder', folderId);
    else params.delete('folder');
    setSearchParams(params);
  };
  const [folderModal, setFolderModal] = useState(null); // 'new' | {id, name}
  const [folderName, setFolderName] = useState('');
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(null);
  const [openMenu, setOpenMenu] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [tab, setTab] = useState('documents');
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [ecForm, setEcForm] = useState(null);
  const [ecSaving, setEcSaving] = useState(false);
  const [deletingEc, setDeletingEc] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [payHistory, setPayHistory] = useState([]);
  const [onboardingAssignments, setOnboardingAssignments] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [ptoBalances, setPtoBalances] = useState([]);
  const [ptoPolicies, setPtoPolicies] = useState([]);
  const [balanceForm, setBalanceForm] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [jobHistoryForm, setJobHistoryForm] = useState(null);
  const [disciplinaryActions, setDisciplinaryActions] = useState([]);
  const [daForm, setDaForm] = useState(null);
  const docInputRef = useRef();
  const dragCounter = useRef(0);

  useEffect(() => {
    api(`/hcm/employees/${id}`).then(r => r.json()).then(setEmployee);
    loadDocuments();
    loadFolders();
    loadCredentials();
    api('/hcm/document-templates').then(r => r.ok ? r.json() : []).then(setTemplates);
    loadEmergencyContacts();
    loadNotes();
    loadPayHistory();
    loadOnboarding();
    loadPtoBalances();
    api('/hcm/pto-policies').then(r => r.ok ? r.json() : []).then(setPtoPolicies);
    loadAudit();
    loadCustomFields();
    loadJobHistory();
    loadDisciplinary();
  }, [id]);

  const loadAudit = () => {
    api(`/hcm/employees/${id}/audit`).then(r => r.ok ? r.json() : []).then(setAuditLog);
  };

  const loadCustomFields = () => {
    api('/hcm/custom-fields').then(r => r.ok ? r.json() : []).then(setCustomFields);
    api(`/hcm/employees/${id}/custom-fields`).then(r => r.ok ? r.json() : []).then(setCustomValues);
  };

  const loadJobHistory = () => {
    api(`/hcm/employees/${id}/job-history`).then(r => r.ok ? r.json() : []).then(setJobHistory);
  };

  const loadDisciplinary = () => {
    api(`/hcm/employees/${id}/disciplinary-actions`).then(r => r.ok ? r.json() : []).then(setDisciplinaryActions);
  };

  const loadEmergencyContacts = () => {
    api(`/hcm/employees/${id}/emergency-contacts`).then(r => r.ok ? r.json() : []).then(setEmergencyContacts);
  };

  const loadNotes = () => {
    api(`/hcm/employees/${id}/notes`).then(r => r.ok ? r.json() : []).then(setNotes);
  };

  const loadPayHistory = () => {
    api(`/hcm/employees/${id}/pay-history`).then(r => r.ok ? r.json() : []).then(setPayHistory);
  };

  const loadOnboarding = () => {
    api(`/hcm/employees/${id}/onboarding`).then(r => r.ok ? r.json() : []).then(setOnboardingAssignments);
    api('/hcm/onboarding-checklists').then(r => r.ok ? r.json() : []).then(setChecklists);
  };

  const loadPtoBalances = () => {
    api(`/hcm/employees/${id}/pto-balances`).then(r => r.ok ? r.json() : []).then(setPtoBalances);
  };

  const loadDocuments = () => {
    api(`/hcm/employees/${id}/documents`).then(r => r.ok ? r.json() : []).then(setDocuments);
  };

  const loadFolders = () => {
    api(`/hcm/employees/${id}/document-folders`).then(r => r.ok ? r.json() : []).then(setFolders);
  };

  const loadCredentials = () => {
    api(`/hcm/employees/${id}/credentials`).then(r => r.ok ? r.json() : []).then(setCredentials);
  };

  const emptyCred = {name: '', issuingOrganization: '', credentialNumber: '', issueDate: '', expirationDate: '', notes: ''};

  const handleCredSave = async (e) => {
    e.preventDefault();
    setCredSaving(true);
    const body = {name: credForm.name, issuingOrganization: credForm.issuingOrganization || null, credentialNumber: credForm.credentialNumber || null,
      issueDate: credForm.issueDate || null, expirationDate: credForm.expirationDate || null, notes: credForm.notes || null};
    const res = credForm.id
      ? await api(`/hcm/employees/${id}/credentials/${credForm.id}`, {method: 'PUT', body: JSON.stringify(body)})
      : await api(`/hcm/employees/${id}/credentials`, {method: 'POST', body: JSON.stringify(body)});
    if (res.ok) {
      const cred = await res.json();
      if (credForm.file) {
        const fd = new FormData();
        fd.append('file', credForm.file);
        const headers = {};
        const jwt = getCookie('jwtToken'); if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
        const tid = getCookie('tenantId'); if (tid) headers['X-Tenant-Id'] = tid;
        await fetch(`${API_BASE}/hcm/employees/${id}/credentials/${cred.id}/file`, {method: 'POST', headers, body: fd});
      }
      setCredForm(null);
      loadCredentials();
    }
    setCredSaving(false);
  };

  const handleCredDelete = async () => {
    await api(`/hcm/employees/${id}/credentials/${deletingCred.id}`, {method: 'DELETE'});
    setDeletingCred(null);
    loadCredentials();
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (currentFolder) formData.append('folderId', currentFolder);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    try {
      await fetch(`${API_BASE}/hcm/employees/${id}/documents`, { method: 'POST', headers, body: formData });
      loadDocuments();
    } finally {
      setUploading(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocUpload = (e) => uploadFile(e.target.files[0]);

  const handleDocDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    uploadFile(e.dataTransfer.files[0]);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    const res = await api(`/hcm/employees/${id}/documents/create`, {
      method: 'POST', body: JSON.stringify({ fileName: createName.trim(), type: createType, folderId: currentFolder })
    });
    setCreateType(null);
    if (res.ok) {
      const doc = await res.json();
      navigate(`/hcm/employees/${id}/documents/${doc.id}/edit`);
    }
  };

  const isEditable = (fileName) => /\.(docx|xlsx|pptx|doc|xls|ppt|odt|ods|odp)$/i.test(fileName);

  useEffect(() => {
    if (!openMenu) return;
    const close = () => setOpenMenu(null);
    const timer = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', close); };
  }, [openMenu]);

  const handleGenerateFromTemplate = async (templateId) => {
    setShowTemplateModal(false);
    await api(`/hcm/employees/${id}/documents/from-template`, {
      method: 'POST', body: JSON.stringify({ templateId, folderId: currentFolder })
    });
    loadDocuments();
  };

  const handleDocDelete = async () => {
    if (!deletingDoc) return;
    await api(`/hcm/employees/${id}/documents/${deletingDoc.id}`, { method: 'DELETE' });
    setDeletingDoc(null);
    loadDocuments();
  };

  const handleFolderSubmit = async (e) => {
    e.preventDefault();
    if (!folderName.trim()) return;
    if (folderModal === 'new') {
      await api(`/hcm/employees/${id}/document-folders`, {
        method: 'POST', body: JSON.stringify({ name: folderName.trim() })
      });
    } else {
      await api(`/hcm/employees/${id}/document-folders/${folderModal.id}`, {
        method: 'PUT', body: JSON.stringify({ name: folderName.trim() })
      });
    }
    setFolderModal(null);
    loadFolders();
  };

  const handleDeleteFolder = async () => {
    if (!deletingFolder) return;
    await api(`/hcm/employees/${id}/document-folders/${deletingFolder.id}`, { method: 'DELETE' });
    setDeletingFolder(null);
    if (currentFolder === deletingFolder.id) setCurrentFolder(null);
    loadFolders();
    loadDocuments();
  };

  const handleTogglePublish = async (docId) => {
    await api(`/hcm/employees/${id}/documents/${docId}/publish`, { method: 'POST' });
    loadDocuments();
  };

  const handleToggleFolderPublish = async (folderId) => {
    await api(`/hcm/employees/${id}/document-folders/${folderId}/publish`, { method: 'POST' });
    loadFolders();
  };

  const handleMoveDoc = async (docId, folderId) => {
    await api(`/hcm/employees/${id}/documents/${docId}`, {
      method: 'PUT', body: JSON.stringify({ folderId })
    });
    loadDocuments();
  };

  const handleDocDownload = async (docId) => {
    const res = await api(`/hcm/employees/${id}/documents/${docId}/url`);
    if (res.ok) {
      const data = await res.json();
      window.open(data.url, '_blank');
    }
  };

  const handleDelete = async () => {
    await api(`/hcm/employees/${id}`, { method: 'DELETE' });
    navigate('/hcm');
  };

  if (!employee) return null;

  const fullName = [employee.title, employee.firstName, employee.displayMiddleName ? employee.middleName : null, employee.lastName, employee.suffix].filter(Boolean).join(' ');

  return (
    <Layout>
      <Link to="/hcm" className="text-indigo-600 hover:text-indigo-800 mb-4 inline-block">&larr; Back to Employees</Link>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 md:p-6 border-b">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{fullName}</h1>
            <div className="flex items-center gap-3">
              <Link to={`/hcm/employees/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
              <button onClick={() => setDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Delete</button>
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Employee ID</p>
            <p className="text-gray-900 font-medium">{employee.employeeId || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Status</p>
            <p className="text-gray-900 font-medium">
              {employee.status ? (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${employee.status === 'active' ? 'bg-green-100 text-green-800' : employee.status === 'terminated' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {employee.status.charAt(0).toUpperCase() + employee.status.slice(1)}
                </span>
              ) : '—'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Job Title</p>
            <p className="text-gray-900 font-medium">{employee.jobTitle || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Department</p>
            <p className="text-gray-900 font-medium">{employee.department || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Employment Type</p>
            <p className="text-gray-900 font-medium">{employee.employmentType || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Manager</p>
            <p className="text-gray-900 font-medium">{employee.managerName || '—'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Hire Date</p>
            <p className="text-gray-900 font-medium">{employee.hireDate || '—'}</p>
          </div>
          {employee.terminationDate && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Termination Date</p>
              <p className="text-gray-900 font-medium">{employee.terminationDate}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-1">Pay</p>
            <p className="text-gray-900 font-medium">
              {employee.payRate ? `$${employee.payRate.toFixed(2)}` : '—'}
              {employee.payType && <span className="text-gray-500 text-sm ml-1">({employee.payType})</span>}
              {employee.payFrequency && <span className="text-gray-500 text-sm ml-1">/ {employee.payFrequency}</span>}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Date of Birth</p>
            <p className="text-gray-900 font-medium">{employee.dateOfBirth || '—'}</p>
          </div>
          {employee.workEmail && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Work Email</p>
              <p className="text-gray-900 font-medium">{employee.workEmail}</p>
            </div>
          )}
          {employee.workPhone && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Work Phone</p>
              <p className="text-gray-900 font-medium">{employee.workPhone}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-1">User Account</p>
            {employee.userId ? (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Mapped
                </span>
                <span className="text-gray-600 text-sm font-mono">{employee.userId}</span>
              </div>
            ) : (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                Not Mapped
              </span>
            )}
          </div>
          {employee.governmentIds && employee.governmentIds.length > 0 && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Government IDs</p>
              <div className="space-y-1">
                {employee.governmentIds.map((govId) => (
                  <div key={govId.id} className="text-gray-900 font-medium">
                    <span className="text-xs text-gray-500">{govId.typeName}:</span> <span className="font-mono text-sm">{govId.idNumber}</span>
                    {(govId.documents || []).map(doc => (
                      <button key={doc.id} onClick={async () => { const r = await api(`/hcm/employees/${id}/government-ids/${govId.id}/documents/${doc.id}`); if (r.ok) { const d = await r.json(); window.open(d.url, '_blank'); } }} className="ml-2 text-indigo-600 hover:text-indigo-800 text-xs">📎 {doc.fileName}</button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-1">Created</p>
            <p className="text-gray-900 font-medium">{new Date(employee.createdAt).toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="flex gap-6 px-1" aria-label="Tabs">
            {[['documents','Documents'],['credentials','Credentials'],['emergency','Emergency Contacts'],['notes','Notes'],['onboarding','Onboarding'],['payhistory','Pay History'],['jobhistory','Job History'],['pto','PTO'],['disciplinary','Disciplinary'],['customfields','Custom Fields'],['audit','Audit Trail'],['addresses','Addresses']].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                  tab === key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}>{label}</button>
            ))}
          </nav>
        </div>
      </div>

      {tab === 'notes' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b">
            <form onSubmit={async e => { e.preventDefault(); if (!newNote.trim()) return; await api(`/hcm/employees/${id}/notes`, {method: 'POST', body: JSON.stringify({content: newNote})}); setNewNote(''); loadNotes(); }} className="flex gap-3">
              <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              <button type="submit" disabled={!newNote.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm">Add</button>
            </form>
          </div>
          {notes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No notes yet.</div>
          ) : (
            <div className="divide-y">
              {notes.map(n => (
                <div key={n.id} className="px-4 md:px-6 py-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-gray-900 whitespace-pre-wrap">{n.content}</p>
                      <p className="text-xs text-gray-400 mt-1">{new Date(n.createdAt).toLocaleString()}</p>
                    </div>
                    <button onClick={async () => { await api(`/hcm/employees/${id}/notes/${n.id}`, {method: 'DELETE'}); loadNotes(); }} className="text-sm text-gray-400 hover:text-red-600">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'payhistory' && (
        <div className="bg-white rounded-lg shadow mt-4">
          {payHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No pay history recorded.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {payHistory.map(p => (
                    <tr key={p.id}>
                      <td className="px-6 py-4 text-gray-900">{p.effectiveDate}</td>
                      <td className="px-6 py-4 text-gray-900">{p.payRate ? `$${p.payRate.toFixed(2)}` : '—'}</td>
                      <td className="px-6 py-4 text-gray-600">{p.payType || '—'}</td>
                      <td className="px-6 py-4 text-gray-600">{p.payFrequency || '—'}</td>
                      <td className="px-6 py-4 text-gray-500 text-sm">{p.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'onboarding' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b flex justify-end">
            {checklists.length > 0 && (
              <select onChange={async e => { if (!e.target.value) return; await api(`/hcm/employees/${id}/onboarding`, {method: 'POST', body: JSON.stringify({checklistId: e.target.value})}); e.target.value = ''; loadOnboarding(); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Assign checklist...</option>
                {checklists.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </div>
          {onboardingAssignments.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No onboarding checklists assigned.</div>
          ) : (
            <div className="divide-y">
              {onboardingAssignments.map(a => (
                <div key={a.id} className="p-4 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-900">{a.checklistName}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {a.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                    </div>
                    <button onClick={async () => { await api(`/hcm/onboarding-assignments/${a.id}`, {method: 'DELETE'}); loadOnboarding(); }} className="text-sm text-gray-400 hover:text-red-600">Remove</button>
                  </div>
                  {a.items && a.items.map(item => (
                    <label key={item.checklistItemId} className="flex items-start gap-3 py-1 cursor-pointer">
                      <input type="checkbox" checked={item.completed} onChange={async () => { await api(`/hcm/onboarding-assignments/${a.id}/toggle/${item.checklistItemId}`, {method: 'POST'}); loadOnboarding(); }}
                        className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                      <div>
                        <span className={`text-sm ${item.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>{item.title}</span>
                        {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                      </div>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'pto' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b">
            <h3 className="font-semibold text-gray-900 mb-3">PTO Balances</h3>
            <div className="flex flex-wrap gap-4 mb-4">
              {ptoBalances.map(b => (
                <div key={b.id} className="border rounded-lg p-3 min-w-[150px]">
                  <p className="text-sm text-gray-500">{b.policyName}</p>
                  <p className="text-xl font-bold text-gray-900">{b.balance}h</p>
                  <p className="text-xs text-gray-400">Used: {b.used}h</p>
                </div>
              ))}
              {ptoBalances.length === 0 && <p className="text-gray-500 text-sm">No balances set.</p>}
            </div>
            <button onClick={() => setBalanceForm({policyId: '', balance: ''})} className="text-sm text-indigo-600 hover:text-indigo-800">Adjust balance</button>
          </div>
        </div>
      )}

      {balanceForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setBalanceForm(null)}>
          <form onSubmit={async e => { e.preventDefault(); await api(`/hcm/employees/${id}/pto-balances`, {method: 'POST', body: JSON.stringify({policyId: balanceForm.policyId, balance: parseFloat(balanceForm.balance)})}); setBalanceForm(null); loadPtoBalances(); }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Adjust PTO Balance</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Policy</label>
              <select required value={balanceForm.policyId} onChange={e => setBalanceForm({...balanceForm, policyId: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select...</option>
                {ptoPolicies.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Balance (hours)</label>
              <input type="number" step="0.5" required value={balanceForm.balance} onChange={e => setBalanceForm({...balanceForm, balance: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setBalanceForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
            </div>
          </form>
        </div>
      )}

      {tab === 'customfields' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6">
            {customFields.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No custom fields defined. Add them in HCM Settings.</p>
            ) : (
              <div className="space-y-4">
                {customFields.map(f => {
                  const val = customValues.find(v => v.fieldId === f.id);
                  return (
                    <div key={f.id}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{f.name}{f.required ? ' *' : ''}</label>
                      {f.fieldType === 'select' ? (
                        <select value={val?.value || ''} onChange={e => {
                          const newVals = customValues.filter(v => v.fieldId !== f.id);
                          newVals.push({fieldId: f.id, value: e.target.value});
                          setCustomValues(newVals);
                        }} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                          <option value="">Select...</option>
                          {(f.options || '').split(',').map(o => <option key={o.trim()} value={o.trim()}>{o.trim()}</option>)}
                        </select>
                      ) : (
                        <input value={val?.value || ''} onChange={e => {
                          const newVals = customValues.filter(v => v.fieldId !== f.id);
                          newVals.push({fieldId: f.id, value: e.target.value});
                          setCustomValues(newVals);
                        }} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                      )}
                    </div>
                  );
                })}
                <button onClick={async () => { await api(`/hcm/employees/${id}/custom-fields`, {method: 'PUT', body: JSON.stringify(customValues)}); }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Save</button>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'audit' && (
        <div className="bg-white rounded-lg shadow mt-4">
          {auditLog.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No audit history.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Old Value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {auditLog.map(a => (
                    <tr key={a.id}>
                      <td className="px-6 py-3 text-sm text-gray-600">{new Date(a.createdAt).toLocaleString()}</td>
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{a.fieldName}</td>
                      <td className="px-6 py-3 text-sm text-red-600">{a.oldValue || '—'}</td>
                      <td className="px-6 py-3 text-sm text-green-600">{a.newValue || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'jobhistory' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b flex justify-end">
            <button onClick={() => setJobHistoryForm({effectiveDate: new Date().toISOString().slice(0,10), jobTitle: employee.jobTitle || '', department: employee.department || '', employmentType: employee.employmentType || '', managerId: employee.managerId || '', changeType: '', notes: ''})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Entry</button>
          </div>
          {jobHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No job history recorded.</div>
          ) : (
            <div className="divide-y">
              {jobHistory.map(j => (
                <div key={j.id} className="px-4 md:px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{j.effectiveDate}</span>
                        {j.changeType && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">{j.changeType}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm text-gray-500">
                        {j.jobTitle && <span>Title: {j.jobTitle}</span>}
                        {j.department && <span>Dept: {j.department}</span>}
                        {j.employmentType && <span>Type: {j.employmentType}</span>}
                        {j.managerName && <span>Manager: {j.managerName}</span>}
                      </div>
                      {j.notes && <p className="text-sm text-gray-400 mt-1 italic">{j.notes}</p>}
                    </div>
                    <button onClick={async () => { await api(`/hcm/employees/${id}/job-history/${j.id}`, {method: 'DELETE'}); loadJobHistory(); }} className="text-sm text-gray-400 hover:text-red-600">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'disciplinary' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b flex justify-end">
            <button onClick={() => setDaForm({date: new Date().toISOString().slice(0,10), severity: 'verbal_warning', type: '', description: '', actionTaken: ''})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Action</button>
          </div>
          {disciplinaryActions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No disciplinary actions recorded.</div>
          ) : (
            <div className="divide-y">
              {disciplinaryActions.map(d => {
                const sevCls = d.severity === 'termination' ? 'bg-red-100 text-red-800' : d.severity === 'final_warning' ? 'bg-orange-100 text-orange-800' : d.severity === 'written_warning' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800';
                return (
                  <div key={d.id} className="px-4 md:px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-gray-900">{d.date}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${sevCls}`}>{d.severity.replace(/_/g, ' ')}</span>
                          <span className="text-sm text-gray-700">{d.type}</span>
                          {d.acknowledged ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Acknowledged</span>
                          ) : (
                            <button onClick={async () => { await api(`/hcm/employees/${id}/disciplinary-actions/${d.id}/acknowledge`, {method: 'POST'}); loadDisciplinary(); }} className="text-xs text-indigo-600 hover:text-indigo-800">Mark Acknowledged</button>
                          )}
                        </div>
                        {d.description && <p className="text-sm text-gray-500 mt-1">{d.description}</p>}
                        {d.actionTaken && <p className="text-sm text-gray-400 mt-1">Action: {d.actionTaken}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setDaForm({id: d.id, date: d.date, severity: d.severity, type: d.type, description: d.description || '', actionTaken: d.actionTaken || ''})} className="text-sm text-gray-400 hover:text-indigo-600">Edit</button>
                        <button onClick={async () => { await api(`/hcm/employees/${id}/disciplinary-actions/${d.id}`, {method: 'DELETE'}); loadDisciplinary(); }} className="text-sm text-gray-400 hover:text-red-600">Delete</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'addresses' && (
        <div className="bg-white rounded-lg shadow mt-4">
          {employee.addresses && employee.addresses.length > 0 ? (
            <div className="p-4 md:p-6 space-y-4">
              {employee.addresses.map((a) => (
                <div key={a.id} className="p-4 border border-gray-200 rounded-lg">
                  {a.types && a.types.length > 0 && <p className="text-sm font-medium text-indigo-600 mb-1">{a.types.map(t => t.name).join(', ')}</p>}
                  {a.line1 && <p className="text-gray-900">{a.line1}</p>}
                  {a.line2 && <p className="text-gray-900">{a.line2}</p>}
                  {a.line3 && <p className="text-gray-900">{a.line3}</p>}
                  <p className="text-gray-900">
                    {[a.city, a.state ? (STATE_LABELS[a.state] || a.state) : null].filter(Boolean).join(', ')}
                    {a.zip ? ` ${a.zip}` : ''}
                  </p>
                  {a.country && <p className="text-gray-500 text-sm">{a.country === 'CA' ? 'Canada' : 'United States'}</p>}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No addresses on file.</div>
          )}
        </div>
      )}

      {tab === 'emergency' && (
        <div className="bg-white rounded-lg shadow mt-4">
          <div className="p-4 md:p-6 border-b flex justify-end">
            <button onClick={() => setEcForm({name: '', relationship: '', phone: '', email: ''})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Contact</button>
          </div>
          {emergencyContacts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No emergency contacts on file.</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {emergencyContacts.map(ec => (
                <div key={ec.id} className="px-4 md:px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{ec.name}</span>
                        {ec.relationship && <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{ec.relationship}</span>}
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm text-gray-500">
                        {ec.phone && <span>{ec.phone}</span>}
                        {ec.email && <span>{ec.email}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button onClick={() => setEcForm({id: ec.id, name: ec.name, relationship: ec.relationship || '', phone: ec.phone || '', email: ec.email || ''})}
                        className="text-sm text-gray-500 hover:text-indigo-600">Edit</button>
                      <button onClick={() => setDeletingEc(ec)} className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'credentials' && (
      <div className="bg-white rounded-lg shadow mt-4">
        <div className="p-4 md:p-6 border-b flex justify-end">
          <button onClick={() => setCredForm({...emptyCred})} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Add Credential</button>
        </div>
        {credentials.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No credentials on file.</div>
        ) : (
          <div className="divide-y divide-gray-200">
            {credentials.map(c => {
              const st = !c.expirationDate ? {label: 'No Expiration', cls: 'bg-gray-100 text-gray-600'}
                : (() => { const d = Math.ceil((new Date(c.expirationDate + 'T00:00:00') - new Date()) / 86400000); return d < 0 ? {label: 'Expired', cls: 'bg-red-100 text-red-800'} : d <= 30 ? {label: `${d}d left`, cls: 'bg-amber-100 text-amber-800'} : d <= 90 ? {label: `${d}d left`, cls: 'bg-yellow-100 text-yellow-800'} : {label: 'Active', cls: 'bg-green-100 text-green-800'}; })();
              return (
                <div key={c.id} className="px-4 md:px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-gray-900">{c.name}</span>
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-6 gap-y-1 mt-1 text-sm text-gray-500">
                        {c.issuingOrganization && <span>{c.issuingOrganization}</span>}
                        {c.credentialNumber && <span>#{c.credentialNumber}</span>}
                        {c.expirationDate && <span>Expires: {c.expirationDate}</span>}
                      </div>
                      {c.notes && <p className="text-sm text-gray-500 mt-1 italic">{c.notes}</p>}
                      {c.fileName && (
                        <button onClick={async () => { const r = await api(`/hcm/employees/${id}/credentials/${c.id}/file-url`); if (r.ok) { const d = await r.json(); window.open(d.url, '_blank'); } }}
                          className="text-sm text-indigo-600 hover:text-indigo-800 mt-1">{c.fileName}</button>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button onClick={() => setCredForm({id: c.id, name: c.name, issuingOrganization: c.issuingOrganization || '', credentialNumber: c.credentialNumber || '', issueDate: c.issueDate || '', expirationDate: c.expirationDate || '', notes: c.notes || ''})}
                        className="text-sm text-gray-500 hover:text-indigo-600">Edit</button>
                      <button onClick={() => setDeletingCred(c)} className="text-sm text-gray-500 hover:text-red-600">Delete</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      )}

      {tab === 'documents' && (
      <div className="bg-white rounded-lg shadow mt-4 relative"
        onDragEnter={e => { e.preventDefault(); dragCounter.current++; setDragging(true); }}
        onDragOver={e => e.preventDefault()}
        onDragLeave={e => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setDragging(false); }}
        onDrop={handleDocDrop}
      >
        {dragging && (
          <div className="absolute inset-0 bg-indigo-50 border-2 border-dashed border-indigo-400 rounded-lg z-10 flex items-center justify-center">
            <p className="text-indigo-600 text-lg font-medium">Drop files here to upload</p>
          </div>
        )}
        <div className="p-4 md:p-6 border-b flex justify-between items-center">
          <div>
            {currentFolder && (
              <nav className="flex items-center gap-1 mt-1 text-sm">
                <button onClick={() => setCurrentFolder(null)}
                  onDragOver={e => { e.preventDefault(); setDragOverFolder('root'); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={e => { e.preventDefault(); setDragOverFolder(null); const docId = e.dataTransfer.getData('text/plain'); if (docId) handleMoveDoc(docId, null); }}
                  className={`text-indigo-600 hover:text-indigo-800 px-1 rounded ${dragOverFolder === 'root' ? 'bg-indigo-100 ring-2 ring-indigo-400' : ''}`}>All Documents</button>
                <span className="text-gray-400">/</span>
                <span className="text-gray-700 font-medium">{folders.find(f => f.id === currentFolder)?.name}</span>
              </nav>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => { setFolderModal('new'); setFolderName(''); }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">New Folder</button>
            <div className="relative">
              <button onClick={() => setShowNew(!showNew)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">New Document ▾</button>
              {showNew && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border z-20">
                  {[['docx','W','Document'],['xlsx','X','Spreadsheet'],['pptx','P','Presentation']].map(([type, icon, label]) => (
                    <button key={type} onClick={() => { setShowNew(false); setCreateType(type); setCreateName(''); }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                      <span className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded flex items-center justify-center font-bold text-sm">{icon}</span>
                      <span className="text-gray-900">{label}</span>
                    </button>
                  ))}
                  {templates.length > 0 && (
                    <>
                      <div className="border-t" />
                      <button onClick={() => { setShowNew(false); setShowTemplateModal(true); }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center gap-3">
                        <span className="w-8 h-8 bg-green-100 text-green-700 rounded flex items-center justify-center font-bold text-sm">T</span>
                        <span className="text-gray-900">From Template</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            <input type="file" ref={docInputRef} onChange={handleDocUpload} className="hidden" />
            <label onClick={() => docInputRef.current.click()}
              className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {uploading ? 'Uploading...' : 'Upload'}
            </label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Size</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visibility</th>
                <th className="px-6 py-3 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {!currentFolder && folders.map(f => (
                <tr key={`f-${f.id}`} className={`hover:bg-gray-50 ${dragOverFolder === f.id ? 'bg-indigo-50 ring-2 ring-inset ring-indigo-400' : ''}`}
                  onDragOver={e => { e.preventDefault(); setDragOverFolder(f.id); }}
                  onDragLeave={() => setDragOverFolder(null)}
                  onDrop={e => { e.preventDefault(); setDragOverFolder(null); const docId = e.dataTransfer.getData('text/plain'); if (docId) handleMoveDoc(docId, f.id); }}>
                  <td className="px-6 py-4">
                    <button onClick={() => setCurrentFolder(f.id)} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium">
                      <svg className="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>
                      {f.name}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-gray-400 hidden sm:table-cell">Folder</td>
                  <td className="px-6 py-4 hidden sm:table-cell">—</td>
                  <td className="px-6 py-4 text-gray-600">{f.createdAt ? new Date(f.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4">
                    <select value={f.published ? 'shared' : 'not_shared'}
                      onChange={e => { if ((e.target.value === 'shared') !== f.published) handleToggleFolderPublish(f.id); }}
                      className={`text-xs px-2 py-1 rounded border-0 cursor-pointer ${f.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      <option value="not_shared">Not Shared</option>
                      <option value="shared">Shared with {employee.firstName}</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="relative">
                      <button onClick={() => setOpenMenu(openMenu === f.id ? null : f.id)} className="text-gray-400 hover:text-gray-600">⋯</button>
                      {openMenu === f.id && (
                        <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border z-10">
                          <button onClick={() => { setOpenMenu(null); setFolderModal({id: f.id, name: f.name}); setFolderName(f.name); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Rename</button>
                          <button onClick={() => { setOpenMenu(null); setDeletingFolder(f); }} className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600">Delete</button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {documents.filter(doc => currentFolder ? doc.folderId === currentFolder : !doc.folderId).map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50" draggable
                  onDragStart={e => e.dataTransfer.setData('text/plain', doc.id)}>
                  <td className="px-6 py-4">
                    {isEditable(doc.fileName) ? (
                      <Link to={`/hcm/employees/${id}/documents/${doc.id}/edit`} className="text-indigo-600 hover:text-indigo-800 font-medium">{doc.fileName}</Link>
                    ) : (
                      <button onClick={() => handleDocDownload(doc.id)} className="text-indigo-600 hover:text-indigo-800 font-medium">{doc.fileName}</button>
                    )}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell"><FileTypeBadge contentType={doc.contentType} fileName={doc.fileName} /></td>
                  <td className="px-6 py-4 text-gray-600 hidden sm:table-cell">{formatSize(doc.size)}</td>
                  <td className="px-6 py-4 text-gray-600">{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="px-6 py-4">
                    <select value={doc.published ? 'shared' : 'not_shared'}
                      onChange={e => { if ((e.target.value === 'shared') !== doc.published) handleTogglePublish(doc.id); }}
                      className={`text-xs px-2 py-1 rounded border-0 cursor-pointer ${doc.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      <option value="not_shared">Not Shared</option>
                      <option value="shared">Shared with {employee.firstName}</option>
                    </select>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDocDownload(doc.id)} className="text-gray-400 hover:text-indigo-600" title="Download">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                      </button>
                      <button onClick={() => setDeletingDoc(doc)} className="text-gray-400 hover:text-red-600" title="Delete">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {folders.length === 0 && documents.length === 0 && <div className="text-center py-12 text-gray-500">No documents yet. Upload a file or create a new document to get started.</div>}
        </div>
      </div>

      )}

      {createType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCreateType(null)}>
          <form onSubmit={handleCreate} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              New {createType === 'docx' ? 'Document' : createType === 'xlsx' ? 'Spreadsheet' : 'Presentation'}
            </h3>
            <input type="text" value={createName} onChange={e => setCreateName(e.target.value)} autoFocus
              placeholder="Enter name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setCreateType(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!createName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">Create</button>
            </div>
          </form>
        </div>
      )}

      {folderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setFolderModal(null)}>
          <form onSubmit={handleFolderSubmit} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{folderModal === 'new' ? 'New Folder' : 'Rename Folder'}</h3>
            <input type="text" value={folderName} onChange={e => setFolderName(e.target.value)} autoFocus
              placeholder="Folder name" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setFolderModal(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={!folderName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">{folderModal === 'new' ? 'Create' : 'Save'}</button>
            </div>
          </form>
        </div>
      )}

      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowTemplateModal(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate from Template</h3>
            <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
              {templates.map(t => (
                <button key={t.id} onClick={() => handleGenerateFromTemplate(t.id)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                  <span className="font-medium text-gray-900">{t.name}</span>
                  <span className="text-xs text-gray-400">Generate</span>
                </button>
              ))}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowTemplateModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deletingDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingDoc(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Document</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingDoc.fileName}"? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingDoc(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDocDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {deletingFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeletingFolder(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Folder</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingFolder.name}"? Documents inside will be moved to the root.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingFolder(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleDeleteFolder} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {credForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setCredForm(null)}>
          <form onSubmit={handleCredSave} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{credForm.id ? 'Edit Credential' : 'Add Credential'}</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credential Name *</label>
              <input required value={credForm.name} onChange={e => setCredForm({...credForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. CPR Certification" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Organization</label>
                <input value={credForm.issuingOrganization} onChange={e => setCredForm({...credForm, issuingOrganization: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credential Number</label>
                <input value={credForm.credentialNumber} onChange={e => setCredForm({...credForm, credentialNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                <input type="date" value={credForm.issueDate} onChange={e => setCredForm({...credForm, issueDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label>
                <input type="date" value={credForm.expirationDate} onChange={e => setCredForm({...credForm, expirationDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={credForm.notes} onChange={e => setCredForm({...credForm, notes: e.target.value})} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
              <input ref={credFileRef} type="file" onChange={e => setCredForm({...credForm, file: e.target.files[0]})}
                className="text-sm text-gray-600" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setCredForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={credSaving || !credForm.name.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {credSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {deletingCred && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setDeletingCred(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Credential</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingCred.name}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingCred(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleCredDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {ecForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setEcForm(null)}>
          <form onSubmit={async e => {
            e.preventDefault(); setEcSaving(true);
            const body = {name: ecForm.name, relationship: ecForm.relationship || null, phone: ecForm.phone || null, email: ecForm.email || null};
            const res = ecForm.id
              ? await api(`/hcm/employees/${id}/emergency-contacts/${ecForm.id}`, {method: 'PUT', body: JSON.stringify(body)})
              : await api(`/hcm/employees/${id}/emergency-contacts`, {method: 'POST', body: JSON.stringify(body)});
            if (res.ok) { setEcForm(null); loadEmergencyContacts(); }
            setEcSaving(false);
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{ecForm.id ? 'Edit' : 'Add'} Emergency Contact</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required value={ecForm.name} onChange={e => setEcForm({...ecForm, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <select value={ecForm.relationship} onChange={e => setEcForm({...ecForm, relationship: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none">
                  <option value="">Select...</option>
                  {['Spouse','Parent','Sibling','Child','Friend','Other'].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input value={ecForm.phone} onChange={e => setEcForm({...ecForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input type="email" value={ecForm.email} onChange={e => setEcForm({...ecForm, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEcForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={ecSaving || !ecForm.name.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {ecSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {deletingEc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setDeletingEc(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Emergency Contact</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete "{deletingEc.name}"?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletingEc(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={async () => { await api(`/hcm/employees/${id}/emergency-contacts/${deletingEc.id}`, {method: 'DELETE'}); setDeletingEc(null); loadEmergencyContacts(); }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {jobHistoryForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setJobHistoryForm(null)}>
          <form onSubmit={async e => { e.preventDefault(); await api(`/hcm/employees/${id}/job-history`, {method: 'POST', body: JSON.stringify(jobHistoryForm)}); setJobHistoryForm(null); loadJobHistory(); }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Add Job History Entry</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date *</label>
              <input type="date" required value={jobHistoryForm.effectiveDate} onChange={e => setJobHistoryForm({...jobHistoryForm, effectiveDate: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Type</label>
              <select value={jobHistoryForm.changeType} onChange={e => setJobHistoryForm({...jobHistoryForm, changeType: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                <option value="">Select...</option>
                <option value="Promotion">Promotion</option>
                <option value="Title Change">Title Change</option>
                <option value="Department Transfer">Department Transfer</option>
                <option value="Type Change">Type Change</option>
                <option value="Demotion">Demotion</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input value={jobHistoryForm.jobTitle} onChange={e => setJobHistoryForm({...jobHistoryForm, jobTitle: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                <input value={jobHistoryForm.department} onChange={e => setJobHistoryForm({...jobHistoryForm, department: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea value={jobHistoryForm.notes} onChange={e => setJobHistoryForm({...jobHistoryForm, notes: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setJobHistoryForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Save</button>
            </div>
          </form>
        </div>
      )}

      {daForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDaForm(null)}>
          <form onSubmit={async e => {
            e.preventDefault();
            if (daForm.id) {
              await api(`/hcm/employees/${id}/disciplinary-actions/${daForm.id}`, {method: 'PUT', body: JSON.stringify({date: daForm.date, severity: daForm.severity, type: daForm.type, description: daForm.description || null, actionTaken: daForm.actionTaken || null})});
            } else {
              await api(`/hcm/employees/${id}/disciplinary-actions`, {method: 'POST', body: JSON.stringify(daForm)});
            }
            setDaForm(null); loadDisciplinary();
          }} onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">{daForm.id ? 'Edit' : 'Add'} Disciplinary Action</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input type="date" required value={daForm.date} onChange={e => setDaForm({...daForm, date: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
                <select required value={daForm.severity} onChange={e => setDaForm({...daForm, severity: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="verbal_warning">Verbal Warning</option>
                  <option value="written_warning">Written Warning</option>
                  <option value="final_warning">Final Warning</option>
                  <option value="suspension">Suspension</option>
                  <option value="termination">Termination</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
              <input required value={daForm.type} onChange={e => setDaForm({...daForm, type: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Attendance, Conduct, Performance" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea value={daForm.description} onChange={e => setDaForm({...daForm, description: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action Taken</label>
              <textarea value={daForm.actionTaken} onChange={e => setDaForm({...daForm, actionTaken: e.target.value})} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDaForm(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">{daForm.id ? 'Save' : 'Create'}</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmModal
        open={deleteModal}
        title="Delete Employee"
        message="Are you sure you want to delete this employee? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal(false)}
      />
    </Layout>
  );
}
